import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const SearchWrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledSearchInput = styled.input`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 12px 16px;
  padding-right: ${(props) => (props.hasValue ? '40px' : '16px')};
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(255,255,255,1)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.15)'};
  border-radius: 8px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#334155')};
  font-size: 14px;
  transition: all 0.15s ease;

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748b')};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(255,255,255,1)'};
    box-shadow: ${(props) =>
      props.theme.name === 'dark'
        ? `0 0 0 1px ${props.theme?.colors?.primary || '#FA4616'}`
        : '0 0 0 3px rgba(250,70,22,0.1)'};
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  transition: color 0.15s ease;

  &:hover {
    color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SearchInput = ({
  onSearch,
  placeholder = 'Search...',
  debounceMs = 500,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);

  // Debounced search - only trigger after user has interacted
  useEffect(() => {
    if (!hasInteracted) return;

    const timer = setTimeout(() => {
      if (onSearch) {
        onSearch(searchTerm);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, onSearch, debounceMs, hasInteracted]);

  const handleChange = (e) => {
    setHasInteracted(true);
    setSearchTerm(e.target.value);
  };

  const handleClear = () => {
    setHasInteracted(true);
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  return (
    <SearchWrapper>
      <StyledSearchInput
        type="text"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        hasValue={searchTerm.length > 0}
      />
      {searchTerm.length > 0 && (
        <ClearButton onClick={handleClear} aria-label="Clear search">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </ClearButton>
      )}
    </SearchWrapper>
  );
};

SearchInput.propTypes = {
  onSearch: PropTypes.func,
  placeholder: PropTypes.string,
  debounceMs: PropTypes.number,
};

export default SearchInput;
